import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Expand_left from "../img/Expand_left.svg";
import refreshLogo from "../img/refreshLogo.svg";
import Expand_down from "../img/Expand_down.svg";
import Close_round from "../img/Close_round.svg";
import Add_round from "../img/Add_round.svg";
import Add_roundWhite from "../img/Add_roundWhite.svg";
import api from "../../api/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import HeaderAddApp from "./HeaderAddApp";

const EditApplication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const [category, setCategory] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [licenseLimits, setLicenseLimits] = useState([]);

  const [appName, setAppName] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedLimits, setSelectedLimits] = useState([]);
  const [features, setFeatures] = useState([
    { heading: "", file: null, description: "", name: "" },
  ]);

  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoFileId, setLogoFileId] = useState(null);
  const [bannerFileId, setBannerFileId] = useState(null);

  const [search, setSearch] = useState("");

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [apiMessage, setApiMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [originalProject, setOriginalProject] = useState(null);
  const [removingFeatureIds, setRemovingFeatureIds] = useState(new Set());
  const [removedFeatureIds, setRemovedFeatureIds] = useState([]);
  const [featureTitle, setFeatureTitle] = useState([]);

  const projectRef = useRef(null);
  const categoryRef = useRef(null);
  const limitRef = useRef(null);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await api.get(`/admin-app/api/v1/read-edit-project/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = res.data?.data;
        const project = data?.project_data;

        if (project) {
          setOriginalProject(project);
          setAppName(project.name || "");
          setTagline(project.tagline || "");
          setFeatureTitle(project.feature_title);
          setDescription(project.description || "");
          setSelectedProjectType(project.project_type || "");
          setSelectedCategory({
            id: project.category_id,
            name: project.category_name,
          });

          setSelectedLimits(project.license_limits || []);

          setFeatures(
            project.features?.length > 0
              ? project.features.map((f) => ({
                  id: f.id || null,
                  heading: f.name || "",
                  name: f.name || "",
                  file: null, // not editable yet, since it's a file input
                  description: f.description || "",
                }))
              : [{ heading: "", file: null, description: "", name: "" }]
          );
        }

        // Also set dropdown choices
        setProjectTypes(data.project_type_choices || []);
        setLicenseLimits(data.license_limit_options || []);
      } catch (error) {
        console.error("Failed to load project data:", error);
        setApiMessage("Failed to load application data");
        setShowErrorModal(true);
      }
    };

    fetchProjectData();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await api.get("/admin-app/api/v1/list-category", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const results = res.data.data.results || [];
      setCategory(results);
      setFilteredCategories(results); // default show all
    } catch (err) {
      console.error("Error fetching categories", err);
    }
  };

  const handleSearch = async (value) => {
    setSearch(value);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await api.get(
        `/admin-app/api/v1/list-category?context=${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setFilteredCategories(res.data.data.results || []);
    } catch (err) {
      console.error("Search failed", err);
    }
  };

  const uploadFile = async (file) => {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/admin-app/api/v1/upload-file", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.status === "success") {
        return res.data.data.id; // image ID from response
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };

  const getUpdatedFields = () => {
    const updatedFields = {};

    // ---------- Top-level fields (unchanged logic: send only when changed) ----------
    if (appName !== originalProject?.name) updatedFields.name = appName;
    if (tagline !== originalProject?.tagline) updatedFields.tagline = tagline;
    if (description !== originalProject?.description)
      updatedFields.description = description;
    if (selectedProjectType !== originalProject?.project_type) {
      updatedFields.project_type = selectedProjectType;
    }

    if (
      selectedCategory?.id &&
      selectedCategory.id !== originalProject?.category_id
    ) {
      updatedFields.category_id = selectedCategory.id;
    }

    // Non-mutating compare for limits
    const newLimitsSorted = [...selectedLimits].sort();
    const originalLimitsSorted = [
      ...(originalProject?.license_limits || []),
    ].sort();
    if (
      JSON.stringify(newLimitsSorted) !== JSON.stringify(originalLimitsSorted)
    ) {
      updatedFields.license_limits = selectedLimits;
    }

    if (logoFileId) updatedFields.logo = logoFileId;
    if (bannerFileId) updatedFields.image = bannerFileId;

    // ---------- Features: send only edited features, with only edited fields ----------
    const origFeatures = Array.isArray(originalProject?.features)
      ? originalProject.features
      : [];
    const currentFeatures = Array.isArray(features) ? features : [];

    // Map originals by id for stable comparison independent of order
    const origById = new Map(origFeatures.map((of) => [of.id, of]));

    // Feature title: compare and include only if changed

    const originalFeatureTitle = originalProject?.feature_title ?? "";
    const currentFeatureTitle = (featureTitle ?? "").trim();

    if (currentFeatureTitle !== originalFeatureTitle) {
      updatedFields.feature_title = currentFeatureTitle;
    }

    // Build per-feature diffs
    const featureUpdates = [];

    // 1) Handle updated or new features
    for (const f of currentFeatures) {
      const trimmedName = (f.name ?? "").trim();
      const trimmedDesc = (f.description ?? "").trim();
      const hasNewLogo = !!f.fileId;

      if (f.id) {
        // existing feature, skip if removed
        if (removedFeatureIds.includes(f.id)) continue;

        const orig = origById.get(f.id) || {};
        const diff = { id: f.id, is_active: true };
        let changed = false;

        if (trimmedName !== (orig.name ?? "")) {
          diff.name = trimmedName;
          changed = true;
        }

        if (trimmedDesc !== (orig.description ?? "")) {
          diff.description = trimmedDesc;
          changed = true;
        }

        if (hasNewLogo) {
          diff.logo = f.fileId;
          changed = true;
        }

        if (changed) {
          featureUpdates.push(diff);
        }
      } else {
        // new feature
        if (!trimmedName) continue;
        featureUpdates.push({
          id: null,
          name: trimmedName,
          description: trimmedDesc,
          ...(hasNewLogo ? { logo: f.fileId } : {}),
          is_active: true,
        });
      }
    }

    // 2) Add removed features as inactive
    for (const removedId of removedFeatureIds) {
      featureUpdates.push({ id: removedId, is_active: false });
    }

    if (featureUpdates.length > 0) {
      updatedFields.features = featureUpdates;
    }

    return updatedFields;
  };

  const handleSubmit = async () => {
    try {
      const updatedFields = getUpdatedFields();

      if (Object.keys(updatedFields).length === 0) {
        setApiMessage("No changes made.");
        setShowSuccessModal(true);
        return;
      }

      const token = localStorage.getItem("accessToken");

      const res = await api.patch(
        `/admin-app/api/v1/read-edit-project/${id}`,
        updatedFields,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.status === "success") {
        setApiMessage("Application updated successfully.");
        setShowSuccessModal(true);
      } else {
        setApiMessage("Failed to update application.");
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error("Update failed:", err);
      setApiMessage("Something went wrong.");
      setShowErrorModal(true);
    }
  };

  const handleRemoveFeature = (index) => {
    const feature = features[index];

    if (feature.id) {
      // Mark this feature as removed
      setRemovedFeatureIds((prev) => [...prev, feature.id]);

      // Optionally hide it in UI
      setFeatures((prev) => prev.filter((_, i) => i !== index));
    } else {
      // For unsaved features, just remove them
      setFeatures((prev) => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="">
      <HeaderAddApp></HeaderAddApp>

      <div className="px-10 py-10 overflow-y-auto max-h-[calc(100vh-64px)]">
        <div className="flex justify-between items-center font-semibold text-sm bg-[#CEC7FF4D] my-3 py-2 px-4 rounded">
          <span>Edit Application Details</span>
          {/* <img src={refreshLogo} alt="" className="bg-white p-1 rounded-md" /> */}
        </div>

        <div className="w-full flex flex-col gap-4 text-sm">
          <div className="w-full flex gap-5">
            <div className="w-1/2 relative" ref={projectRef}>
              <label htmlFor="" className="text-sm font-semibold ">
                Application Type
              </label>
              <div
                onClick={() => setActiveDropdown("project")}
                className={`pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter flex items-center justify-between cursor-pointer mt-1 ${
                  selectedProjectType === "" ? "text-[#9CA3AF]" : "text-black"
                }`}
                style={{
                  backgroundImage: `url(${Expand_down})`,
                  backgroundPosition: "98% center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {selectedProjectType || "Select Application Type"}
              </div>

              {activeDropdown === "project" && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-[#8C8C8C4D] rounded-md shadow-sm max-h-56 overflow-y-auto">
                  {projectTypes.map((type, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setSelectedProjectType(type);
                        setActiveDropdown(null);
                      }}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    >
                      {type}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="w-1/2 relative" ref={categoryRef}>
              {/* Visible Dropdown Trigger */}
              <label htmlFor="" className="text-sm font-semibold ">
                Category
              </label>
              <div
                onClick={() => {
                  setActiveDropdown("category");
                  setFilteredCategories(category); // show all when opened
                  setSearch("");
                }}
                className={`pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] mt-1 bg-transparent rounded-md font-inter flex items-center justify-between cursor-pointer ${
                  selectedCategory === "" ? "text-[#9CA3AF]" : "text-black"
                }`}
                style={{
                  backgroundImage: `url(${Expand_down})`,
                  backgroundPosition: "98% center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {selectedCategory?.name || "Category"}
              </div>

              {/* Dropdown content */}
              {activeDropdown === "category" && (
                <div
                  className="absolute z-10 mt-1 w-full bg-white border border-[#8C8C8C4D] rounded-md shadow-sm max-h-56 overflow-y-auto"
                  style={{ maxHeight: "250px", overflowY: "auto" }}
                >
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:outline-none"
                    placeholder="Search Category..."
                  />

                  {filteredCategories.length === 0 ? (
                    <div className="p-2 text-sm text-gray-500">
                      No categories found
                    </div>
                  ) : (
                    filteredCategories.map((cat) => (
                      <div
                        key={cat.id}
                        onClick={() => {
                          setSelectedCategory(cat);
                          setSearch(cat.name);
                          setActiveDropdown(null);
                        }}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        {cat.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="w-full flex gap-5">
            <div className="w-1/2 flex flex-col">
              <label htmlFor="" className="text-sm font-semibold ">
                Application Name
              </label>
              <input
                type="text"
                className=" pl-5 h-10 pr-4 mt-1  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none "
                placeholder="Application Name"
                value={appName}
                maxLength={40}
                onChange={(e) => setAppName(e.target.value)}
                name=""
              />
            </div>

            <div className="flex flex-col w-1/2">
              <label htmlFor="" className="text-sm font-semibold ">
                Tagline
              </label>
              <input
                type="text"
                className="mt-1 pl-5 h-10 pr-4  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none "
                placeholder="Add Tagline"
                name=""
                maxLength={100}
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
              />
            </div>
          </div>
          <div className="w-full">
            <label htmlFor="" className="text-sm font-semibold ">
              Description
            </label>
            <textarea
              name=""
              id=""
              placeholder="Description"
              value={description}
              maxLength={330}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-1 pl-5 pt-2 h-16 pr-4  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none "
            ></textarea>
          </div>
          <div className="w-full flex sm:flex-row flex-col sm:gap-5 gap-4">
            <div className="sm:w-1/2 w-full">
              <span className="text-sm font-semibold ">Application Icon</span>
              <div className="w-full mt-1 px-1 flex items-center gap-6  h-10 pr-4  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none ">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="logoUpload"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    setLogoFile(file);

                    if (file) {
                      const uploadedId = await uploadFile(file);
                      setLogoFileId(uploadedId);
                    }
                  }}
                />

                <label
                  htmlFor="logoUpload"
                  className="cursor-pointer bg-primary rounded-md px-3 py-1 text-white hover:scale-105 transition-transform duration-300"
                  style={{ willChange: "transform" }}
                >
                  Choose File
                </label>

                <span className="text-[#9CA3AF]">
                  {logoFile ? logoFile.name : ""}
                </span>
              </div>
            </div>

            <div className="sm:w-1/2 w-full">
              <span className="text-sm font-semibold ">Banner Image</span>
              <div className="w-full mt-1 px-1 flex items-center gap-6  h-10 pr-4  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none ">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="bannerUpload"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    setBannerFile(file);

                    if (file) {
                      const uploadedId = await uploadFile(file);
                      setBannerFileId(uploadedId);
                    }
                  }}
                />

                <label
                  htmlFor="bannerUpload" // ✅ fix this line
                  className="cursor-pointer bg-primary rounded-md px-3 py-1 text-white hover:scale-105 transition-transform duration-300"
                  style={{ willChange: "transform" }}
                >
                  Choose File
                </label>

                <span className="text-[#9CA3AF]">
                  {bannerFile ? bannerFile.name : ""}
                </span>
              </div>
            </div>
          </div>

          <div className="w-full flex gap-5">
            <div className="w-full flex flex-col gap-4">
              <div className="flex justify-between items-center font-semibold text-sm bg-[#CEC7FF4D] py-2 px-4 rounded">
                <span>App Limitss</span>
              </div>
              <div className="flex gap-4">
                <div className="w-1/2 relative" ref={limitRef}>
                  <label htmlFor="" className="text-sm font-semibold ">
                    Limit Type
                  </label>
                  <div
                    onClick={() => setActiveDropdown("limit")}
                    className={`pl-5 mt-1 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter flex items-center justify-between cursor-pointer ${
                      selectedLimits === "" ? "text-[#9CA3AF]" : "text-black"
                    }`}
                    style={{
                      backgroundImage: `url(${Expand_down})`,
                      backgroundPosition: "98% center",
                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    <span className="text-[#9CA3AF]">Select Limit Type</span>
                  </div>

                  {activeDropdown === "limit" && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-[#8C8C8C4D] rounded-md shadow-sm max-h-56 overflow-y-auto">
                      {licenseLimits.map((limit, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            if (selectedLimits.includes(limit)) {
                              setSelectedLimits(
                                selectedLimits.filter((l) => l !== limit)
                              ); // remove
                            } else {
                              setSelectedLimits([...selectedLimits, limit]); // add
                            }
                            setActiveDropdown(null); // close dropdown
                          }}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                          {limit}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <span className="w-1/2"></span>
              </div>

              <div className="flex gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                  {selectedLimits.length === 0 ? (
                    <span className="text-[#9CA3AF]">No limits selected yet.</span>
                  ) : (
                    selectedLimits.map((limit, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between  gap-4  pl-5 h-10 pr-3  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter bg-right bg-no-repeat "
                      >
                        <span>{limit}</span>
                        <img
                          src={Close_round}
                          alt=""
                          onClick={() =>
                            setSelectedLimits(
                              selectedLimits.filter((l) => l !== limit)
                            )
                          }
                          className="cursor-pointer"
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex gap-5">
            <div className="w-full flex flex-col gap-4">
              <div className="flex justify-between items-center font-semibold text-sm bg-[#CEC7FF4D]  py-2 px-4 rounded">
                <span>App Features</span>
              </div>
              <div className="flex gap-4">
                <div className="w-full flex flex-col">
                  <label htmlFor="" className="text-sm font-semibold ">
                    Feature Heading
                  </label>
                  <input
                    type="text"
                    placeholder="Feature Heading"
                    maxLength={90}
                    className="w-full mt-1 pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                    value={featureTitle}
                    onChange={(e) => setFeatureTitle(e.target.value)}
                  />
                </div>
              </div>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="relative p-5 rounded-2xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  {/* Floating remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(index)}
                    className="absolute top-1 right-1 w-7 h-7 flex items-center justify-center 
      rounded-full bg-primary text-white shadow-lg 
      hover:bg-white hover:text-primary hover:scale-110 hover:rotate-12 border border-primary 
      transition-all duration-300"
                    aria-label="Remove feature"
                    title="Remove feature"
                  >
                    {/* Trash icon (inline SVG) */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>

                  {/* Card Content */}
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Left Column */}
                    <div className="flex flex-col w-full gap-4">
                      {/* Feature Title */}
                      <div className=" flex flex-col">
                        <label htmlFor="" className="text-sm font-semibold ">
                          Feature Title
                        </label>
                        <input
                          type="text"
                          placeholder="Feature Title"
                          maxLength={30}
                          className="pl-5 mt-1 h-10 pr-4 border-2 border-gray-300 bg-gray-50 rounded-lg font-inter 
          focus:outline-none "
                          value={feature.name}
                          onChange={(e) => {
                            const updated = [...features];
                            updated[index].name = e.target.value;
                            setFeatures(updated);
                          }}
                        />
                      </div>

                      {/* File Upload */}
                      <div
                        className="w-full px-1 flex items-center gap-4 h-10 pr-4 border-2 border-gray-300 
        bg-gray-50 rounded-md font-inter"
                      >
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id={`featureLogo_${index}`}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            const updated = [...features];
                            updated[index].file = file;

                            if (file) {
                              const uploadedId = await uploadFile(file);
                              updated[index].fileId = uploadedId;
                            }

                            setFeatures(updated);
                          }}
                        />

                        <label
                          htmlFor={`featureLogo_${index}`}
                          className="cursor-pointer flex items-center gap-2 bg-primary rounded-md px-4 py-1 text-white 
            hover:bg-primary-dark transition-all duration-300 hover:scale-105 transition-transform duration-300"
                          style={{ willChange: "transform" }}
                        >
                          {/* Image icon (inline SVG) */}
                          Choose File
                        </label>

                        <span className="text-gray-500 text-sm truncate flex-1">
                          {feature.file ? feature.file.name : "No file chosen"}
                        </span>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="flex flex-col w-full">
                      <label htmlFor="" className="text-sm font-semibold ">
                        Description
                      </label>
                      <textarea
                        placeholder="Long Description"
                        maxLength={330}
                        className="w-full mt-1 pl-5 pt-3 h-28 pr-4 border-2 border-gray-300 bg-gray-50 rounded-md 
        font-inter focus:outline-none focus:ring-2 focus:ring-primary"
                        value={feature.description}
                        onChange={(e) => {
                          const updated = [...features];
                          updated[index].description = e.target.value;
                          setFeatures(updated);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="flex justify-between items-center w-40 cursor-pointer rounded-md py-2 bg-[#CEC7FF4D] px-2 text-primary hover:bg-primary hover:scale-105 transition-transform duration-300 hover:text-white group"
            onClick={() =>
              setFeatures([
                ...features,
                { heading: "", file: null, description: "" },
              ])
            }
            style={{ willChange: "transform" }}
          >
            <img src={Add_round} alt="" className="block group-hover:hidden" />
            <img
              src={Add_roundWhite}
              alt=""
              className="group-hover:block hidden"
            />
            <span className="">Add New Feature</span>
          </div>

          <div>
            <button
              className="bg-primary w-64 py-2 text-white font-semibold rounded-md float-right hover:scale-105 transition-transform duration-300"
              style={{ willChange: "transform" }}
              onClick={handleSubmit}
            >
              Save
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showSuccessModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black bg-opacity-50"
              />
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                  initial={{ scale: 0.9, y: 20, opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1 }}
                  exit={{ scale: 0.9, y: 20, opacity: 0 }}
                  className="w-full max-w-md bg-white rounded-xl shadow-xl"
                >
                  <div className="flex justify-center pt-8">
                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50">
                      <motion.div
                        className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50"
                        animate={{
                          rotate: [0, -10, 10, -10, 10, 0], // shake motion
                        }}
                        transition={{
                          duration: 0.6,
                          ease: "easeInOut",
                        }}
                      >
                        <svg
                          className="w-12 h-12 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </motion.div>
                    </div>
                  </div>
                  <div className="px-6 py-4 text-center">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      Success!
                    </h3>
                    <p className="text-gray-600 mb-6">{apiMessage}</p>
                  </div>
                  <div className="px-6 pb-6">
                    <button
                      onClick={() => {
                        setShowSuccessModal(false);
                        navigate(-1);
                      }}
                      className="w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg"
                    >
                      Continue
                    </button>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showErrorModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black bg-opacity-50"
              />
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                  initial={{ scale: 0.9, y: 20, opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1 }}
                  exit={{ scale: 0.9, y: 20, opacity: 0 }}
                  className="w-full max-w-md bg-white rounded-xl shadow-xl"
                >
                  <div className="flex justify-center pt-8">
                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50">
                      {/* Close (X) icon instead of checkmark */}
                      <motion.div
                        className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50"
                        animate={{
                          rotate: [0, -10, 10, -10, 10, 0], // shake motion
                        }}
                        transition={{
                          duration: 0.6,
                          ease: "easeInOut",
                        }}
                      >
                        <svg
                          className="w-8 h-8 text-red-500"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeWidth={2}
                            strokeLinecap="round"
                            d="M12 8v4m0 4h.01M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"
                          />
                        </svg>
                      </motion.div>
                    </div>
                  </div>
                  <div className="px-6 py-4 text-center">
                    <p className="text-gray-600 mb-6">{apiMessage}</p>
                  </div>
                  <div className="px-6 pb-6">
                    <button
                      onClick={() => setShowErrorModal(false)}
                      className="w-full py-3 px-4 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg"
                    >
                      Try Again
                    </button>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>

        {/* Loading overlay */}
      </div>
    </div>
  );
};

export default EditApplication;
